import React, { useCallback, useEffect, useRef, useState } from 'react';

import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';

import { Col, DatePicker, Drawer, Form, Row, Select } from 'antd';
import moment from 'moment';

import BoxContainer from '@src/components/BoxContainer';
import Title from '@src/components/BoxContainer/components/Title';
import ElasticPaginatedSelect from '@src/components/ElasticPaginatedSelect';
import FilterContainer from '@src/components/FilterContainer';
import { FORM_LAYOUT_DEFAULT } from '@src/constants/commons';
import { FSCollections } from '@src/constants/database';
import { ABSENCE_TYPES } from '@src/containers/App/TimeTracker/Main/components/reports/shared/constants';
import { formatUserFullName } from '@src/util/format/userFullName';
import IntlMessages from '@src/util/IntlMessages';

import {
  ABSENCE_USER_SEARCH_DEBOUNCE_MS,
  ABSENCE_USER_SEARCH_MIN_LENGTH,
  IS_EXCUSED_TYPES,
  requiresExcusedAbsence,
} from '../../constants';

const parseUserOptions = (response, cache) =>
  (response?.hits?.hits ?? []).map(hit => {
    const source = hit._source ?? {};
    const value = String(hit._id ?? source.id ?? '');
    const user = {
      id: value,
      firstName: source.firstName || '',
      lastName: source.lastName || '',
    };
    if (value) cache.set(value, user);
    return {
      value,
      label: formatUserFullName(user) || source.email || value,
    };
  });

const AbsenceFormDrawer = ({ visible, onClose, absence, onSave }) => {
  const intl = useIntl();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const userCacheRef = useRef(new Map());

  const organizationId = useSelector(({ organizations }) => organizations.organization?.id);

  const watchStartDate = Form.useWatch('startDate', form);
  const watchType = Form.useWatch('type', form);
  const typeRequiresExcused = requiresExcusedAbsence(watchType);

  const handleTypeChange = type => {
    if (requiresExcusedAbsence(type)) {
      form.setFieldsValue({ isExcused: true });
    }
    form.validateFields(['isExcused']).catch(() => undefined);
  };

  const handleUserChange = useCallback(value => {
    if (typeof value !== 'string' || !value) {
      setSelectedUser(null);
      return;
    }

    // Only accept Elastic parseData / edit-mode cache hits (full summary).
    const cached = userCacheRef.current.get(value);
    setSelectedUser(cached ?? null);
  }, []);

  useEffect(() => {
    if (visible && absence) {
      const userId = absence.user?.id;
      if (userId) {
        const userSummary = {
          id: userId,
          firstName: absence.user.firstName || '',
          lastName: absence.user.lastName || '',
        };
        userCacheRef.current.set(userId, userSummary);
        setSelectedUser(userSummary);
      } else {
        setSelectedUser(null);
      }

      form.setFieldsValue({
        ...absence,
        user: userId,
        startDate: absence.startDate ? moment(absence.startDate) : null,
        endDate: absence.endDate ? moment(absence.endDate) : null,
        isExcused: requiresExcusedAbsence(absence.type) ? true : absence.isExcused,
      });
    } else if (visible) {
      form.resetFields();
      setSelectedUser(null);
      userCacheRef.current = new Map();
    }
  }, [visible, absence, form]);

  const handleSubmit = async values => {
    const resolvedUser = selectedUser?.id
      ? userCacheRef.current.get(selectedUser.id)
      : undefined;
    if (!resolvedUser) {
      form.setFields([
        {
          name: 'user',
          errors: [intl.formatMessage({ id: 'absences.userUnresolved' })],
        },
      ]);
      return;
    }

    setLoading(true);
    try {
      const valuesToSave = {
        ...values,
        user: resolvedUser,
      };
      await onSave(valuesToSave);
      onClose();
    } catch (error) {
      console.error('Error saving absence:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Drawer title={null} width={720} open={visible} destroyOnClose closable={false}>
      <BoxContainer fullContent shadow fixed>
        <FilterContainer
          title={
            <Title.Header
              value={<IntlMessages id={absence ? 'absences.edit' : 'absences.create'} />}
            />
          }
          buttonItems={[
            {
              iconName: 'save',
              action: form.submit,
              disabled: loading,
            },
          ]}
          goBack={onClose}
        />
      </BoxContainer>
      <BoxContainer fullContent>
        <Form
          form={form}
          layout={FORM_LAYOUT_DEFAULT}
          onFinish={handleSubmit}
          initialValues={{
            status: true,
            isExcused: false,
          }}
        >
          <Row gutter={16}>
            <Col span={24}>
              <Title.LabelForm required value={<IntlMessages id="absences.type" />} />
              <Form.Item
                name="type"
                rules={[
                  {
                    required: true,
                    message: intl.formatMessage({ id: 'form.required' }),
                  },
                ]}
              >
                <Select
                  allowClear
                  showSearch
                  optionFilterProp="children"
                  onChange={handleTypeChange}
                >
                  {ABSENCE_TYPES.map(item => (
                    <Select.Option key={item.code} value={item.code}>
                      {item.label}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={12} lg={12} xl={12} xxl={12}>
              <Title.LabelForm required value={<IntlMessages id="general.user" />} />
              <Form.Item
                name="user"
                rules={[
                  {
                    required: true,
                    message: intl.formatMessage({ id: 'form.required' }),
                  },
                ]}
              >
                <ElasticPaginatedSelect
                  className="gx-w-100"
                  entity={FSCollections.USERS}
                  organizationId={organizationId || ''}
                  enabled={Boolean(organizationId) && visible}
                  searchKey={['firstName', 'lastName', 'email']}
                  debounceMs={ABSENCE_USER_SEARCH_DEBOUNCE_MS}
                  minSearchLength={ABSENCE_USER_SEARCH_MIN_LENGTH}
                  pageSize={10}
                  allowClear
                  placeholder={intl.formatMessage({
                    id: 'absences.userSearchPlaceholder',
                  })}
                  parseData={response => parseUserOptions(response, userCacheRef.current)}
                  onChange={handleUserChange}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={12} lg={12} xl={12} xxl={12}>
              <Title.LabelForm required value={<IntlMessages id="absences.excused.absence" />} />
              <Form.Item
                name="isExcused"
                rules={[
                  {
                    required: true,
                    message: intl.formatMessage({ id: 'form.required' }),
                  },
                  {
                    validator: (_, value) => {
                      if (requiresExcusedAbsence(form.getFieldValue('type')) && value !== true) {
                        return Promise.reject(
                          new Error(intl.formatMessage({ id: 'absences.excused.requiredForType' })),
                        );
                      }
                      return Promise.resolve();
                    },
                  },
                ]}
              >
                <Select>
                  {IS_EXCUSED_TYPES.map(item => (
                    <Select.Option
                      key={String(item.value)}
                      value={item.value}
                      disabled={typeRequiresExcused && item.value === false}
                    >
                      {intl.formatMessage({ id: item.label })}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={12} lg={12} xl={12} xxl={12}>
              <Title.LabelForm required value={<IntlMessages id="general.start.date" />} />
              <Form.Item
                name="startDate"
                rules={[
                  {
                    required: true,
                    message: intl.formatMessage({ id: 'form.required' }),
                  },
                ]}
              >
                <DatePicker className="gx-w-100" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={12} lg={12} xl={12} xxl={12}>
              <Title.LabelForm required value={<IntlMessages id="general.end.date" />} />
              <Form.Item
                name="endDate"
                rules={[
                  {
                    required: true,
                    message: intl.formatMessage({ id: 'form.required' }),
                  },
                ]}
              >
                <DatePicker
                  className="gx-w-100"
                  disabledDate={current => current.isBefore(watchStartDate)}
                />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </BoxContainer>
    </Drawer>
  );
};

export default AbsenceFormDrawer;
